/*
 * SILVER GARDENS — GLOBAL INCLUDED POT DIFFERENCES V1
 * Only adjusts the customer-facing pot-card labels for legacy selectors.
 * Shopify variant prices, item linkage, product gallery, cart and quantity
 * remain fully owned by their established controllers.
 * Native V4.4, bundle builder and mini sets have their own supported pricing.
 */
(() => {
  'use strict';
  const selectors = ['.sg-single-pots', '.sg-linked-pots'];
  const money = amount => {
    const clean = Math.abs(Math.round(Number(amount) * 100) / 100);
    return 'Dhs. ' + clean.toLocaleString('en-AE', {
      minimumFractionDigits: Number.isInteger(clean) ? 0 : 2,
      maximumFractionDigits: 2
    });
  };
  const amount = formatted => {
    const match = String(formatted || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : null;
  };
  const activeGroup = root =>
    root.querySelector('[data-sg-pot-group].is-active') ||
    root.querySelector('[data-sg-pot-group]:not([hidden])');
  const potCards = group => [...group.querySelectorAll('[data-sg-pot-card]')];
  const swatch = (card, colour) => {
    const enabled = [...card.querySelectorAll('[data-sg-pot-swatch],[data-sg-pot-colour]')]
      .filter(node => !node.disabled && node.getAttribute('data-available') !== 'false');
    return enabled.find(node => node.getAttribute('data-colour') === colour) ||
      enabled.find(node => node.classList.contains('is-active')) ||
      enabled[0] || null;
  };
  const cardKey = (card, legacy) => {
    if (card.hasAttribute('data-sg-nursery')) return 'nursery';
    if (!legacy) return card.getAttribute('data-sg-selector-group') || '';
    return card.getAttribute('data-sg-product-id') || '';
  };
  const cardAmount = (card, colour, legacy) => {
    if (card.hasAttribute('data-sg-nursery')) return 0;
    const selected = swatch(card, colour);
    const value = selected?.getAttribute(legacy ? 'data-price' : 'data-pot-price');
    return amount(value || (legacy ?
      card.querySelector('[data-sg-card-price]')?.textContent :
      card.querySelector('[data-sg-pot-price]')?.textContent));
  };
  const setLabel = (node, diff) => {
    if (!node || !Number.isFinite(diff)) return;
    const output = Math.abs(diff) < 0.005 ? 'Included' :
      (diff > 0 ? '+ ' : '− ') + money(diff);
    if (node.textContent.trim() !== output) node.textContent = output;
  };
  const state = new WeakMap();
  const refresh = root => {
    if (!root.isConnected) return;
    const legacy = root.classList.contains('sg-linked-pots');
    const group = activeGroup(root);
    if (!group) return;
    const cards = potCards(group);
    if (!cards.length) return;
    let reference = state.get(root);
    if (!reference) {
      const chosen = cards.find(c => c.classList.contains('is-selected')) ||
        cards.find(c => c.hasAttribute('data-sg-nursery')) || cards[0];
      const key = cardKey(chosen, legacy);
      const current = swatch(chosen, '');
      reference = {
        key,
        colour: current?.getAttribute('data-colour') || ''
      };
      state.set(root, reference);
    }
    let chosenReferenceCard = cards.find(c => cardKey(c, legacy) === reference.key);
    // If a pot is unavailable for a changed plant size, use the selected pot
    // for that size. This avoids displaying a difference from an invalid item.
    if (!chosenReferenceCard) chosenReferenceCard =
      cards.find(c => c.classList.contains('is-selected')) ||
      cards.find(c => c.hasAttribute('data-sg-nursery')) || cards[0];
    const base = cardAmount(chosenReferenceCard, reference.colour, legacy);
    if (base === null) return;
    cards.forEach(card => {
      const node = legacy ?
        card.querySelector('[data-sg-card-price],.sg-linked-pot-card__price') :
        card.querySelector('[data-sg-pot-price],.sg-pot-card__price--nursery');
      const cardPrice = cardAmount(card, '', legacy);
      if (cardPrice === null) return;
      setLabel(node, cardPrice - base);
    });
  };
  const scheduled = new WeakSet();
  const later = root => {
    if (scheduled.has(root)) return;
    scheduled.add(root);
    requestAnimationFrame(() => {
      scheduled.delete(root);
      refresh(root);
    });
  };
  const all = () => document.querySelectorAll(selectors.join(','))
    .forEach(root => later(root));
  const init = () => requestAnimationFrame(() => requestAnimationFrame(all));
  document.addEventListener('click', event => {
    const root = event.target.closest(selectors.join(','));
    if (root) later(root);
    else if (event.target.closest('[data-sg-size]')) all();
  });
  document.addEventListener('change', all);
  document.addEventListener('product:variant-change', all);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();